import React from "react";
import { Link } from "react-router-dom";
import { Wheat, Snowflake, Factory, Building2, Shirt, ArrowRight } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

function IndustryCard({ icon: Icon, title, body, testId }) {
  const t = useT();
  return (
    <div data-testid={testId} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-card-soft">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF] mb-4">
        <Icon size={22} />
      </div>
      <h3 className="font-heading text-lg text-graphite mb-2">{t(title)}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{t(body)}</p>
    </div>
  );
}

export default function Industries() {
  const t = useT();
  return (
    <div>
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-[#00D4FF] mb-3">{t("Industries we serve")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">{t("Built for the plants that keep")} <span className="text-gradient-brand">{t("India")}</span> {t("running.")}</h1>
          <p className="mt-6 text-white/75 max-w-2xl">{t("We started with rice mills — the anchor of our region's industrial base. Today we work with any facility where electricity is a top-3 cost line.")}</p>
        </div>
      </section>

      {/* Rice mills — anchor */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-widest text-[#FFC857] mb-3 inline-flex items-center gap-2"><Wheat size={14} /> {t("Anchor segment")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl text-graphite">{t("Rice mills")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{t("Rice mills run heavy motor loads — hullers, polishers, elevators, dryers — with high daytime consumption. That's exactly the load shape solar pairs with best. On top of that, most mills quietly pay two hidden costs: ")}<b>{t("demand charges")}</b>{t(" that don't match their real load and ")}<b>{t("power factor penalties")}</b>{t(" from unbalanced motor loads.")}</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#00D4FF]" /> {t("High daytime load = high solar offset potential")}</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#21F59B]" /> {t("EMS Lite catches PF drops before they become penalties")}</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#FFC857]" /> {t("Bill Scan often finds mis-categorised tariff slabs")}</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#00D4FF]" /> {t("Zero capex — production budget stays intact")}</li>
            </ul>
            <div className="mt-8">
              <Link to="/contact" data-testid="ricemill-cta" className="btn-gradient inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm">{t("Talk to us about your mill")} <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-mist p-8 border border-slate-100">
              <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-4">{t("Typical mill load lines")}</div>
              <div className="space-y-3">
                {[
                  ["Huller", 62],
                  ["Polisher", 78],
                  ["Elevator", 34],
                  ["Dryer", 88],
                  ["Aux + lighting", 22],
                ].map(([name, pct]) => (
                  <div key={name}>
                    <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                      <span>{t(name)}</span><span>{pct}{t("% peak")}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-wz-gradient" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-slate-500">{t("Illustrative load fingerprint. Your survey will produce the real one.")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Other industries */}
      <section className="bg-mist py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-3">{t("Also serving")}</div>
            <h2 className="font-heading text-3xl text-graphite">{t("Adjacent industries with the same pain.")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <IndustryCard testId="ind-cold" icon={Snowflake} title="Cold storage" body="24×7 compressor loads with expensive night-tariff exposure. Ripe for demand and PF optimisation." />
            <IndustryCard testId="ind-agro" icon={Wheat} title="Agro-processing" body="Seasonal peaks, motor-heavy lines. Solar covers daytime, EMS Lite manages the peaks." />
            <IndustryCard testId="ind-textile" icon={Shirt} title="Textiles" body="Continuous process loads with steep tariff slabs. Small PF improvements = large recoveries." />
            <IndustryCard testId="ind-commercial" icon={Building2} title="Commercial buildings" body="Offices, malls, hospitals — HVAC and lighting profiles that map neatly onto solar." />
          </div>
        </div>
      </section>

      {/* Case study placeholder */}
    </div>
  );
}
